import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { CartItem } from '../models';

interface CartStore {
  items: CartItem[];
  addItem: (item: CartItem) => 'added' | 'updated' | 'max_stock';
  removeItem: (productId: number, variantId: number) => void;
  updateQuantity: (productId: number, variantId: number, quantity: number) => void;
  toggleGift: (productId: number, variantId: number) => void;
  clearCart: () => void;
  totalItems: () => number;
  totalPrice: () => number;
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],

      addItem: (item) => {
        const existing = get().items.find(
          (i) => i.productId === item.productId && i.variantId === item.variantId
        );
        if (existing) {
          const newQty = existing.quantity + item.quantity;
          if (newQty > item.stockQuantity) return 'max_stock';
          set((state) => ({
            items: state.items.map((i) =>
              i.productId === item.productId && i.variantId === item.variantId
                ? { ...i, quantity: newQty, stockQuantity: item.stockQuantity }
                : i
            ),
          }));
          return 'updated';
        }
        if (item.quantity > item.stockQuantity) return 'max_stock';
        set((state) => ({ items: [...state.items, item] }));
        return 'added';
      },

      removeItem: (productId, variantId) =>
        set((state) => ({
          items: state.items.filter(
            (i) => !(i.productId === productId && i.variantId === variantId)
          ),
        })),

      updateQuantity: (productId, variantId, quantity) =>
        set((state) => ({
          items: state.items.map((i) => {
            if (i.productId !== productId || i.variantId !== variantId) return i;
            const capped = Math.min(quantity, i.stockQuantity);
            return { ...i, quantity: Math.max(1, capped) };
          }),
        })),

      toggleGift: (productId, variantId) =>
        set((state) => ({
          items: state.items.map((i) =>
            i.productId === productId && i.variantId === variantId
              ? { ...i, isGift: !i.isGift }
              : i
          ),
        })),

      clearCart: () => set({ items: [] }),

      totalItems: () => get().items.reduce((sum, i) => sum + i.quantity, 0),

      totalPrice: () =>
        get().items.reduce(
          (sum, i) => sum + (i.promotionalPrice ?? i.price) * i.quantity,
          0
        ),
    }),
    { name: 'babychic-cart' }
  )
);
